import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import { Virtuoso } from 'react-virtuoso'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { FadeOverflow } from '@root/shared/ui-kit'

import {
  commentsHistoryPeriodAtom,
  commentsHistoryTypeAtom,
  reatomCommentsHistory,
} from '../../atoms'

import { CommentsHistoryEmpty } from './comments-history-empty'
import { CommentsHistoryError } from './comments-history-error'
import { CommentsHistoryLoading } from './comments-history-loading'
import { CommentsHistoryThread } from './comments-history-thread'

import type { Comment } from '@root/entities/comment'
import type { DelugeId } from '@root/entities/user-short'
import type { ClassNameProps } from '@root/shared/react'

export type CommentsHistoryProps = {
  delugeId: DelugeId
  model?: ReturnType<typeof reatomCommentsHistory>
} & ClassNameProps

export const CommentsHistory = ({
  className,
  delugeId,
  model: initialModel,
}: CommentsHistoryProps) => {
  const [type] = useAtom(commentsHistoryTypeAtom)
  const [period] = useAtom(commentsHistoryPeriodAtom)
  const [network] = useAtom(selectedNetworkAtom)

  const model = useMemo(() => {
    if (initialModel) {
      return initialModel
    }

    // eslint-disable-next-line @reatom/unit-naming-rule
    return reatomCommentsHistory({
      delugeId,
      type,
      period,
      network: Network.toDto(network),
    })
  }, [type, period, network, delugeId, initialModel])

  const [history] = useAtom(model.historyAtom)
  const loadCommentsMore = useAction(model.loadCommentsMoreAction)
  const [loadCommentsRequestState] = useAtom(model.loadCommentsRequestState)
  const [hasMoreItems] = useAtom(model.hasMoreItemsAtom)
  const reloadComments = useAction(model.reloadCommentsAction)

  const isEmpty =
    history.length === 0 && !AsyncStateN.isLoading(loadCommentsRequestState)

  if (AsyncStateN.isError(loadCommentsRequestState)) {
    return <CommentsHistoryError onRetry={reloadComments} />
  }
  if (isEmpty) {
    return <CommentsHistoryEmpty />
  }

  return (
    <>
      <Root
        mode="always"
        variant="vertical"
        blockStart={4}
        blockEnd={16}
        className={className}
      >
        <RootVirtuoso
          increaseViewportBy={300}
          components={{
            List,
            Footer: () =>
              AsyncStateN.isLoading(loadCommentsRequestState) ? (
                <CommentsHistoryLoading />
              ) : undefined,
          }}
          data={history}
          totalCount={history.length}
          // data-empty={!hasMoreItems}
          endReached={() => {
            if (AsyncStateN.isError(loadCommentsRequestState)) {
              return
            }

            if (hasMoreItems) {
              loadCommentsMore()
            }
          }}
          itemContent={(_, history) => (
            <CommentsHistoryThread
              key={history.pair.address}
              history={history}
            />
          )}
        />
      </Root>
    </>
  )
}

const Root = styled(FadeOverflow)`
  overflow: auto;
  display: grid;
  align-items: start;
`

const RootVirtuoso = styled(Virtuoso<Comment.CommentsHistory>)``

const List = styled.div`
  display: grid;
  gap: 14px;
  padding-inline-end: 4px;
  margin-bottom: 14px;
`
